/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For mor information visit: https://egtuniverse.com/legal
 * 
 * Created for Universe App Tools.
 * 
 * Created by Justin K Kazmierczak.
 */
var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.appbar",
  title: "Side Nav Bar",
  description: "A generic app shell sidebar powered by navbar items and optional nested sections",
  fields: {
    items: {
      title: "Items",
      type: "inner",
      namespaces: ["ua.navbar.items"],
      description: "Top-level navigation items.",
      default: []
    },
    sections: {
      title: "Sections",
      type: "list",
      of: "object",
      description: "Collapsible navigation sections with child navbar items.",
      fields: {
        id: { title: "ID", type: "string", description: "Stable section identifier for persisted open-state." },
        title: { title: "Title", type: "string", description: "Visible label shown for the navigation section." },
        icon: { title: "Icon", type: "icon", description: "Optional icon displayed next to the section title." },
        open: { title: "Open", type: "boolean", description: "Whether the section starts expanded.", default: false },
        spacerBefore: { title: "Spacer Before", type: "boolean", description: "Insert a spacer row before this section.", default: false },
        spacerAfter: { title: "Spacer After", type: "boolean", description: "Insert a spacer row after this section.", default: false },
        items: {
          title: "Items",
          type: "inner",
          namespaces: ["ua.navbar.items"],
          description: "Navbar items rendered inside this section.",
          default: []
        }
      },
      default: []
    },
    header: {
      title: "Header",
      type: "inner",
      description: "Content shown at the top of the sidebar."
    },
    footer: {
      title: "Footer",
      type: "inner",
      description: "Content shown at the bottom of the sidebar."
    },
    inner: {
      title: "Inner",
      type: "inner",
      description: "Main content rendered beside the sidebar.",
      required: true
    },
    src: {
      title: "Source",
      type: "string",
      description: "Optional source file to load additional top-level items from."
    },
    hideSpacer: {
      title: "Hide Spacer",
      type: "boolean",
      description: "Hide the default top spacer in the sidebar.",
      default: false
    },
    mobileMenu: {
      title: "Mobile Menu",
      type: "boolean",
      description: "Show a mobile menu trigger using ua.navbar.menu.",
      default: true
    },
    mobileMenuMode: {
      title: "Mobile Menu Mode",
      type: "string",
      description: "How mobile menu items are generated from appbar navigation.",
      default: "layered",
      list: {
        must: ["layered", "flat"]
      }
    },
    mobileMenuOpenCurrentPath: {
      title: "Mobile Menu Open Current Path",
      type: "boolean",
      description: "Open the matching submenu for current URL path when launching mobile menu.",
      default: true
    },
    menuTitle: {
      title: "Mobile Menu Title",
      type: "string",
      description: "Title for the mobile menu trigger.",
      default: "Menu"
    },
    menuIcon: {
      title: "Mobile Menu Icon",
      type: "icon",
      description: "Icon for the mobile menu trigger.",
      default: "list"
    },
    singleOpen: {
      title: "Single Open",
      type: "boolean",
      description: "When true, only one section can stay expanded at a time.",
      default: true
    },
    rememberOpenSection: {
      title: "Remember Open Section",
      type: "boolean",
      description: "Remember the user's most recently used section for the current path.",
      default: true
    },
    openSectionStorageKey: {
      title: "Open Section Storage Key",
      type: "string",
      description: "Storage key used to remember the last open section.",
      default: "ua.appbar.open.section"
    },
    sectionSpacerClass: {
      title: "Section Spacer Class",
      type: "class",
      description: "Optional class for spacer rows inserted between sections."
    },
    sidebarClass: {
      title: "Sidebar Class",
      type: "class",
      description: "Additional class list for the sidebar container."
    },
    contentClass: {
      title: "Content Class",
      type: "class",
      description: "Additional class list for the content container."
    },
    sidebarAttributes: {
      title: "Sidebar Attributes",
      type: "object",
      description: "Attributes to apply to the sidebar container."
    },
    contentAttributes: {
      title: "Content Attributes",
      type: "object",
      description: "Attributes to apply to the content container."
    }
  }
});

var uai = require("../interface/interface.js");
var events = require("../../../uam/events.js");

function toArray(value) {
  if (value instanceof Array) return value;
  if (value === undefined || value === null || value === "") return [];
  return [value];
}

function mergeClass(baseClass, extraClass) {
  var out = (baseClass || "").toString().trim();
  var add = (extraClass || "").toString().trim();
  if (!add) return out;
  if (!out) return add;
  return out + " " + add;
}

function normalizeIcon(value) {
  var raw = (value || "").toString().trim();
  if (!raw) return "";
  if (raw.indexOf("bi ") === 0) raw = raw.split(/\s+/).pop() || "";
  if (raw.indexOf("bi-") === 0) return raw.substring(3);
  return raw;
}

function normalizeNavItem(input, className) {
  if (!(input) || typeof input !== "object") return null;

  var out = { ...input };

  if (!(out.n) && !(out.namespace) && out.src) {
    out.n = "ua.navbar.item";
  }

  if (out.n === "ua.navbar.item" || out.namespace === "ua.navbar.item") {
    if (!out.n) out.n = "ua.navbar.item";
    if (out.icon) out.icon = normalizeIcon(out.icon);
    out.class = mergeClass(out.class, className || "");
    return out;
  }

  return out;
}

function flattenMenuItems(items, sections) {
  var out = [];

  var top = toArray(items);
  for (var i = 0; i < top.length; i++) {
    var normalized = normalizeNavItem(top[i], "ua-app-bar-menu-item");
    if (normalized) out.push(normalized);
  }

  var sectionRows = toArray(sections);
  for (var s = 0; s < sectionRows.length; s++) {
    var section = sectionRows[s] || {};
    var title = (section.title || "").toString().trim();
    if (title) {
      out.push({
        n: "div",
        c: "ua-app-bar-menu-section-title text-uppercase small text-muted pt-2 ps-2",
        i: title
      });
    }

    var sectionItems = toArray(section.items);
    for (var si = 0; si < sectionItems.length; si++) {
      var sectionItem = normalizeNavItem(sectionItems[si], "ua-app-bar-menu-item");
      if (sectionItem) out.push(sectionItem);
    }
  }

  return out;
}

function buildLayeredMenuItems(items, sections) {
  var out = [];

  var top = toArray(items);
  for (var i = 0; i < top.length; i++) {
    var normalizedTop = normalizeNavItem(top[i], "ua-app-bar-menu-item");
    if (normalizedTop) out.push(normalizedTop);
  }

  var sectionRows = toArray(sections);
  for (var s = 0; s < sectionRows.length; s++) {
    var section = sectionRows[s] || {};
    var sectionItems = toArray(section.items).map(function(item) {
      return normalizeNavItem(item, "ua-app-bar-menu-item");
    }).filter(Boolean);
    if (sectionItems.length === 0) continue;

    var sectionTitle = (section.title || "").toString().trim() || ("Section " + (s + 1));
    var sectionIcon = normalizeIcon(section.icon || "grid");

    out.push({
      n: "ua.navbar.item",
      src: "#",
      title: sectionTitle,
      icon: sectionIcon || "grid",
      type: "icon",
      class: "ua-app-bar-menu-item",
      menu: {
        type: "halfscreen",
        inner: sectionItems
      }
    });
  }

  return out;
}

function applyAttributes(target, attrs) {
  if (!(attrs) || typeof attrs !== "object") return target;
  var keys = Object.keys(attrs);
  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    if (!key) continue;
    if (key === "class" || key === "i" || key === "inner" || key === "n" || key === "namespace") continue;
    target[key] = attrs[key];
  }
  return target;
}

function normalizePathValue(pathValue) {
  var raw = (pathValue || "").toString().trim();
  if (!raw) return "/";
  var noQuery = raw.split("?")[0].split("#")[0];
  if (!noQuery) return "/";
  if (noQuery.length > 1 && noQuery.endsWith("/")) return noQuery.slice(0, -1);
  return noQuery;
}

function resolveHrefPath(href) {
  var raw = (href || "").toString().trim();
  if (!raw) return "";
  if (raw.indexOf("#") === 0) return "";

  try {
    var url = new URL(raw, window.location.origin);
    return normalizePathValue(url.pathname || "/");
  } catch (error) {
    return normalizePathValue(raw);
  }
}

function sectionHasCurrentPath(sectionNode, currentPath) {
  if (!(sectionNode) || !(currentPath)) return false;
  var links = sectionNode.querySelectorAll("a[href]");
  for (var i = 0; i < links.length; i++) {
    var linkPath = resolveHrefPath(links[i].getAttribute("href"));
    if (linkPath && linkPath === currentPath) return true;
  }
  return false;
}

function closeOtherSections(sections, keepId) {
  for (var i = 0; i < sections.length; i++) {
    var section = sections[i];
    var id = section.getAttribute("data-ua-appbar-section-id") || "";
    if (id !== keepId) {
      section.open = false;
    }
  }
}

function isDesktopViewport() {
  if (typeof window === "undefined" || !(window.matchMedia)) return true;
  return window.matchMedia("(min-width: 768px)").matches;
}

function closeSiblingDetails(detailNode) {
  if (!(detailNode) || !(detailNode.parentElement)) return;
  var parent = detailNode.parentElement;
  var siblings = parent.children || [];
  for (var i = 0; i < siblings.length; i++) {
    var sibling = siblings[i];
    if (sibling === detailNode) continue;
    if ((sibling.tagName || "").toLowerCase() !== "details") continue;
    sibling.open = false;
  }
}

function enforceSingleOpenInContainer(container) {
  if (!(container) || !(container.children)) return;
  var children = container.children;
  var firstOpen = null;
  for (var i = 0; i < children.length; i++) {
    var child = children[i];
    if ((child.tagName || "").toLowerCase() !== "details") continue;
    if (child.open === true) {
      if (!(firstOpen)) firstOpen = child;
      else child.open = false;
    }
  }
}

function enforceNestedSingleOpenDefaults(appBar) {
  if (!(appBar)) return;
  var containers = appBar.querySelectorAll(".ua-app-bar-items, .ua-app-bar-section-items, .dashboard-nav-group-items");
  for (var i = 0; i < containers.length; i++) {
    enforceSingleOpenInContainer(containers[i]);
  }
}

events.on("interface.afterrender", async function() {
  var appBars = document.querySelectorAll(".ua-app-bar[data-ua-appbar='true']");
  if (!(appBars) || appBars.length === 0) return;

  for (var a = 0; a < appBars.length; a++) {
    var appBar = appBars[a];
    var sections = appBar.querySelectorAll("details[data-ua-appbar-section-id]");
    if (!(sections) || sections.length === 0) continue;

    var singleOpen = appBar.getAttribute("data-ua-appbar-single-open") !== "false";
    var rememberOpen = appBar.getAttribute("data-ua-appbar-remember-open") !== "false";
    var storageKey = appBar.getAttribute("data-ua-appbar-storage-key") || "ua.appbar.open.section";
    var pageStorageKey = storageKey + ":" + normalizePathValue(window.location.pathname || "/");
    var currentPath = normalizePathValue(window.location.pathname || "/");

    var rememberedSectionId = "";
    if (rememberOpen) {
      try {
        rememberedSectionId = (sessionStorage.getItem(pageStorageKey) || "").toString().trim();
      } catch (error) {
        rememberedSectionId = "";
      }
    }

    var firstMatchingSection = null;
    var rememberedMatchingSection = null;
    var firstOpenSection = null;

    for (var s = 0; s < sections.length; s++) {
      var section = sections[s];
      var sectionId = (section.getAttribute("data-ua-appbar-section-id") || "").toString().trim();

      if (!(firstOpenSection) && section.open === true) firstOpenSection = section;

      if (sectionHasCurrentPath(section, currentPath)) {
        if (!(firstMatchingSection)) firstMatchingSection = section;
        if (rememberedSectionId && sectionId === rememberedSectionId) {
          rememberedMatchingSection = section;
        }
      }
    }

    var targetSection = rememberedMatchingSection || firstMatchingSection || firstOpenSection || sections[0];
    if (targetSection) {
      targetSection.open = true;
      if (singleOpen && isDesktopViewport()) {
        closeOtherSections(sections, targetSection.getAttribute("data-ua-appbar-section-id") || "");
      }
    }

    if (singleOpen && isDesktopViewport()) {
      enforceNestedSingleOpenDefaults(appBar);
    }

    for (var i = 0; i < sections.length; i++) {
      var sectionNode = sections[i];
      var sectionNodeId = (sectionNode.getAttribute("data-ua-appbar-section-id") || "").toString().trim();

      sectionNode.addEventListener("toggle", function() {
        if (!(singleOpen)) return;
        if (!isDesktopViewport()) return;
        if (this.open !== true) return;

        var openId = this.getAttribute("data-ua-appbar-section-id") || "";
        closeOtherSections(sections, openId);

        if (rememberOpen) {
          try {
            sessionStorage.setItem(pageStorageKey, openId);
          } catch (error) {}
        }
      });

      var links = sectionNode.querySelectorAll("a[href]");
      for (var li = 0; li < links.length; li++) {
        links[li].addEventListener("click", function() {
          if (!(rememberOpen)) return;
          var owner = this.closest("details[data-ua-appbar-section-id]");
          if (!(owner)) return;
          var ownerId = owner.getAttribute("data-ua-appbar-section-id") || "";
          if (!(ownerId)) return;
          try {
            sessionStorage.setItem(pageStorageKey, ownerId);
          } catch (error) {}
        });
      }

      if (rememberOpen && sectionNode.open === true && sectionNodeId) {
        try {
          sessionStorage.setItem(pageStorageKey, sectionNodeId);
        } catch (error) {}
      }
    }

    var nestedDetails = appBar.querySelectorAll(".ua-app-bar-items details");
    for (var nd = 0; nd < nestedDetails.length; nd++) {
      nestedDetails[nd].addEventListener("toggle", function() {
        if (!(singleOpen)) return;
        if (!isDesktopViewport()) return;
        if (this.open !== true) return;
        closeSiblingDetails(this);
      });
    }
  }
});

exports.render = async function(options) {
  var opts = options || {};

  var items = toArray(opts.items);
  var sections = toArray(opts.sections);

  if ("src" in opts) {
    var file = await uai.getObject(opts.src, "json");
    if (file) {
      items = items.concat(toArray(file));
    }
  }

  var sidebarItems = [];

  if (!(opts.hideSpacer)) {
    sidebarItems.push({ n: "div", c: "ua-app-bar-spacer", i: "\u00a0" });
  }

  if (opts.header) {
    sidebarItems.push({ n: "div", c: "ua-app-bar-header", i: opts.header });
  }

  for (var i = 0; i < items.length; i++) {
    var item = normalizeNavItem(items[i], "ua-app-bar-item");
    if (item) sidebarItems.push(item);
  }

  for (var s = 0; s < sections.length; s++) {
    var section = sections[s] || {};
    var sectionId = (section.id || section.key || ("section-" + s)).toString().trim() || ("section-" + s);
    var sectionTitle = (section.title || "").toString().trim() || "Section";
    var sectionIcon = normalizeIcon(section.icon || "grid");
    var sectionItems = toArray(section.items).map(function(row) {
      return normalizeNavItem(row, "ua-app-bar-subitem");
    }).filter(Boolean);

    if (sectionItems.length === 0) continue;

    var summaryInner = [
      {
        n: "span",
        c: "ua-app-bar-section-summary-left",
        i: [
          { n: "i", c: "bi bi-" + (sectionIcon || "grid") },
          { n: "span", i: sectionTitle }
        ]
      },
      { n: "i", c: "bi bi-chevron-down ua-app-bar-section-chevron" }
    ];

    var sectionClass = mergeClass("ua-app-bar-section", section.class || "");

    if (section.spacerBefore === true) {
      sidebarItems.push({
        n: "div",
        c: mergeClass("ua-app-bar-section-spacer", opts.sectionSpacerClass || "")
      });
    }

    var detailNode = {
      n: "details",
      c: sectionClass || "ua-app-bar-section",
      "data-ua-appbar-section-id": sectionId,
      i: [
        { n: "summary", c: "ua-app-bar-section-summary", i: summaryInner },
        { n: "div", c: "ua-app-bar-section-items", i: sectionItems }
      ]
    };

    if (section.open === true) {
      detailNode.open = true;
    }

    sidebarItems.push(detailNode);

    if (section.spacerAfter === true) {
      sidebarItems.push({
        n: "div",
        c: mergeClass("ua-app-bar-section-spacer", opts.sectionSpacerClass || "")
      });
    }
  }

  if (opts.footer) {
    sidebarItems.push({ n: "div", c: "ua-app-bar-footer", i: opts.footer });
  }

  var mobileMenuEnabled = opts.mobileMenu !== false;
  var mobileMenuMode = (opts.mobileMenuMode || "layered").toString().trim().toLowerCase();
  var mobileMenuItems = mobileMenuMode === "flat"
    ? flattenMenuItems(items, sections)
    : buildLayeredMenuItems(items, sections);
  var mobileMenuNode = null;

  if (mobileMenuEnabled && mobileMenuItems.length > 0) {
    mobileMenuNode = {
      n: "div",
      c: "ua-app-bar-mobile d-md-none",
      i: {
        n: "ua.navbar.item",
        src: "#",
        type: "icon-only",
        title: (opts.menuTitle || "Menu").toString(),
        icon: normalizeIcon(opts.menuIcon || "list"),
        class: mergeClass("ua-app-bar-mobile-button", opts.mobileMenuOpenCurrentPath === false ? "" : "ua-navbar-menu-open-current"),
        menu: {
          type: "halfscreen",
          inner: mobileMenuItems
        }
      }
    };
  }

  var sidebarNode = {
    n: "div",
    c: mergeClass("ua-app-bar-items", opts.sidebarClass || "") || "ua-app-bar-items",
    i: sidebarItems
  };
  applyAttributes(sidebarNode, opts.sidebarAttributes);

  var contentNode = {
    n: "div",
    c: mergeClass("ua-app-bar-inner", opts.contentClass || "") || "ua-app-bar-inner",
    i: mobileMenuNode ? [mobileMenuNode].concat(toArray(opts.inner)) : opts.inner
  };
  applyAttributes(contentNode, opts.contentAttributes);

  var appBar = {
    n: "div",
    c: mergeClass("ua-app-bar", opts.class || "") || "ua-app-bar",
    "data-ua-appbar": "true",
    "data-ua-appbar-single-open": opts.singleOpen === false ? "false" : "true",
    "data-ua-appbar-remember-open": opts.rememberOpenSection === false ? "false" : "true",
    "data-ua-appbar-storage-key": (opts.openSectionStorageKey || "ua.appbar.open.section").toString(),
    i: [sidebarNode, contentNode]
  };

  return appBar;
};
